/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on Jul 03, 2026 by hoarfroster
 *
 */

import { GoodDate } from '@utils/datetime';
import httpRequest from '@utils/http-request';
import { asArray, asBoolean, asNumber, asRecord, asString } from '@models/json-utils';

const API_BASE = import.meta.env.VITE_SERVER_ENDPOINT;

export class ActivityBoardSection {
    id: string;
    projectId: string;
    name: string;
    description: string;
    color: number;
    position: number;
    isDefault: boolean;
    isCompleted: boolean;
    systemKey?: string;

    constructor(raw: Partial<ActivityBoardSection> = {}) {
        this.id = raw.id ?? '';
        this.projectId = raw.projectId ?? '';
        this.name = raw.name ?? '';
        this.description = raw.description ?? '';
        this.color = raw.color ?? 0x64748b;
        this.position = raw.position ?? 0;
        this.isDefault = raw.isDefault ?? false;
        this.isCompleted = raw.isCompleted ?? false;
        this.systemKey = raw.systemKey;
    }

    static fromJSON = (json: unknown): ActivityBoardSection => {
        const r = asRecord(json);
        return new ActivityBoardSection({
            id: asString(r.id),
            projectId: asString(r.projectId),
            name: asString(r.name),
            description: asString(r.description),
            color: asNumber(r.color, 0x64748b),
            position: asNumber(r.position),
            isDefault: asBoolean(r.isDefault),
            isCompleted: asBoolean(r.isCompleted),
            systemKey: r.systemKey == null ? undefined : asString(r.systemKey),
        });
    };
}

export class ActivityProject {
    id: string;
    name: string;
    description: string;
    color: number;
    position: number;
    isDefault: boolean;
    createdAt: GoodDate;
    updatedAt: GoodDate;
    deletedAt?: GoodDate;
    sections: ActivityBoardSection[];

    constructor(
        id: string,
        name: string,
        description: string,
        color: number,
        position: number,
        isDefault: boolean,
        createdAt: number,
        updatedAt: number,
        deletedAt?: number,
        sections: ActivityBoardSection[] = []
    ) {
        this.id = id;
        this.name = name;
        this.description = description;
        this.color = color;
        this.position = position;
        this.isDefault = isDefault;
        this.createdAt = GoodDate.fromEpochSeconds(createdAt);
        this.updatedAt = GoodDate.fromEpochSeconds(updatedAt);
        this.deletedAt = deletedAt ? GoodDate.fromEpochSeconds(deletedAt) : undefined;
        this.sections = sections.slice().sort((a, b) => a.position - b.position);
    }

    static fromJSON(json: unknown): ActivityProject {
        const r = asRecord(json);
        return new ActivityProject(
            asString(r.id),
            asString(r.name, ''),
            asString(r.description, ''),
            asNumber(r.color, 0x3b82f6),
            asNumber(r.position, 0),
            asBoolean(r.isDefault, false),
            asNumber(r.createdAt, GoodDate.nowInEpochSeconds()),
            asNumber(r.updatedAt, GoodDate.nowInEpochSeconds()),
            r.deletedAt === undefined || r.deletedAt === null ? undefined : asNumber(r.deletedAt),
            asArray(r.sections ?? [], ActivityBoardSection.fromJSON)
        );
    }

    static fromJSONList(json: unknown): ActivityProject[] {
        if (!Array.isArray(json)) return [];
        return json.map((project) => ActivityProject.fromJSON(project));
    }

    static template(): ActivityProject {
        const now = GoodDate.nowInEpochSeconds();
        return new ActivityProject('', '', '', 0x3b82f6, 0, false, now, now);
    }

    static list = async (): Promise<ActivityProject[]> => {
        const response = await httpRequest(`${API_BASE}/time/projects`, 'GET');
        const json = await response.json();
        if (response.status === 200) {
            return ActivityProject.fromJSONList(json.data);
        }
        throw new Error(json.error);
    };

    static save = async (id: string, project: ActivityProject): Promise<ActivityProject> => {
        const response = await httpRequest(`${API_BASE}/time/projects${id ? `/${id}` : ''}`, id ? 'PUT' : 'POST', {
            name: project.name,
            description: project.description,
            color: project.color,
            position: project.position,
            isDefault: project.isDefault,
            sections: project.sections.map((section) => ({
                id: section.id,
                name: section.name,
                description: section.description,
                color: section.color,
                position: section.position,
                isDefault: section.isDefault,
                isCompleted: section.isCompleted,
            })),
        });
        const json = await response.json();
        if (response.status === 200) {
            return ActivityProject.fromJSON(json.data);
        }
        throw new Error(json.error);
    };

    static delete = async (id: string): Promise<void> => {
        const response = await httpRequest(`${API_BASE}/time/projects/${id}`, 'DELETE');
        if (response.status !== 200) {
            throw new Error((await response.json()).error);
        }
    };

    static saveSection = async (projectId: string, section: ActivityBoardSection): Promise<ActivityBoardSection> => {
        const sectionId = section.id || 'new';
        const response = await httpRequest(`${API_BASE}/time/projects/${projectId}/sections/${sectionId}`, 'PUT', {
            name: section.name,
            description: section.description,
            color: section.color,
            position: section.position,
            isDefault: section.isDefault,
            isCompleted: section.isCompleted,
        });
        const json = await response.json();
        if (response.status === 200) return ActivityBoardSection.fromJSON(json.data);
        throw new Error(json.error);
    };

    static deleteSection = async (projectId: string, sectionId: string): Promise<void> => {
        const response = await httpRequest(`${API_BASE}/time/projects/${projectId}/sections/${sectionId}`, 'DELETE');
        if (response.status !== 200) throw new Error((await response.json()).error);
    };
}
